'use client';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { Children, forwardRef, useMemo, useRef, useState } from 'react';
import { useCombinedRefs, useSafeLayoutEffect } from '../../util';
import GridContainer from './GridContainer';
import { gapSizes } from './types';
import { calcColumns, useScrollToKeyboardFocus } from './utils';
export const DynamicGrid = /*#__PURE__*/forwardRef(({
  minWidth,
  autoFit,
  maxColumns = Infinity,
  maxRows,
  modulo = 1,
  gapSize = 1,
  elementRole = 'none',
  scroll,
  children,
  ...rest
}, fRef) => {
  const innerRef = useRef();
  const combinedRef = useCombinedRefs(fRef, innerRef);
  const [elementWidth, setElementWidth] = useState(0);
  useSafeLayoutEffect(() => {
    setElementWidth(innerRef.current.clientWidth);
    if (!window.ResizeObserver) return;
    const observer = new ResizeObserver(entries => window.requestAnimationFrame(() => setElementWidth(entries[0].contentRect.width)));
    observer.observe(innerRef.current);
    return () => observer.disconnect();
  }, []);
  const columns = useMemo(() => calcColumns({
    minWidth,
    elementWidth,
    gap: gapSizes[gapSize],
    modulo,
    maxColumns,
    scrollHint: scroll === null || scroll === void 0 ? void 0 : scroll.scrollHint
  }), [minWidth, elementWidth, gapSize, modulo, maxColumns, scroll === null || scroll === void 0 ? void 0 : scroll.scrollHint]);
  useScrollToKeyboardFocus(innerRef, scroll);
  const childrenAsList = useMemo(() => Children.map(children, child => /*#__PURE__*/React.createElement("li", null, child)), [children]);
  return /*#__PURE__*/React.createElement(GridContainer, _extends({
    $minWidth: minWidth,
    $autoFit: autoFit,
    $maxRows: maxRows,
    $columns: columns,
    $gap: gapSizes[gapSize],
    $scroll: scroll,
    role: elementRole,
    ref: combinedRef
  }, rest, {
    "data-aui": "dynamic-grid"
  }), elementRole === 'list' ? childrenAsList : children);
});